// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ImageModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPrimaryVideo, {
    ProductPrimaryVideoStyles,
} from "@west/App/Components/ProductVideoPlayerDetails/ProductPrimaryVideo";
import VideoPlayer from "@west/App/Components/ProductVideoPlayerDetails/VideoPlayer";
import { isVideoType } from "@west/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // XNG-Changes
    headerIconProps?: IconPresentationProps;
    backToTop?: LinkPresentationProps;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    primaryVideo?: ProductPrimaryVideoStyles;
    specificationsVideoModal?: ModalPresentationProps;
    attributeTextWrapper?: InjectableCss;
    documentAccordionSection?: AccordionSectionPresentationProps;
    attributeAccordionSection?: AccordionSectionPresentationProps;
    videoAccordionSection?: AccordionSectionPresentationProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
};

const styles = specificationsStyles;
const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, images },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [image, setImage] = React.useState<ImageModel | undefined>(undefined);
    const [mediaReady, setMediaReady] = React.useState(false);

    const renderingDocuments = showDocuments && documents && documents.length > 0;
    // XNG-Changes: Remove Brand from renderingAttributes
    const renderingAttributes = showAttributes && attributeTypes && attributeTypes?.length > 0;
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";
    const renderingVideos = images && images.some(o => isVideoType(o.imageType));

    const handleBackToTop = () => {
        typeof window !== "undefined" && window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const openModal = (img: any) => {
        setImage(img);
        setModalIsOpen(true);
    };

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    const productDetailsSpecification = specifications?.find(specification => specification.sortOrder === 1);

    const getSpecificationElement = (specification: SpecificationModel, initialExpanded?: boolean) => {
        return (
            <ManagedAccordionSection
                initialExpanded={initialExpanded}
                {...{ stopRowClickable: true }}
                title={
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.title}>{specification.nameDisplay}</Typography>
                        <Link
                            className="top"
                            onClick={handleBackToTop}
                            {...styles.backToTop}
                            aria-label={translate("Back to Top")}
                        >
                            {translate("Back To Top")}
                        </Link>
                    </StyledWrapper>
                }
                {...{ stopRowClickable: true }}
                data-test-selector="productDetails_specifications_specification"
                {...styles.specificationsAccordionSection}
            >
                {parse(transformContentHtml(specification.htmlContent), parserOptions)}
            </ManagedAccordionSection>
        );
    };

    const documentsAccordion = renderingDocuments && (
        <ManagedAccordionSection
            title={
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>{translate("Documents")}</Typography>
                    <Link
                        className="top"
                        onClick={handleBackToTop}
                        {...styles.backToTop}
                        aria-label={translate("Back to Top")}
                    >
                        {translate("Back To Top")}
                    </Link>
                </StyledWrapper>
            }
            {...{ stopRowClickable: true }}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={
                ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
                (!renderingSpecifications && !renderingAttributes)
            }
            {...{ stopRowClickable: true }}
            {...styles.documentAccordionSection}
        >
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </ManagedAccordionSection>
    );

    const attributesAccordion = renderingAttributes && (
        <ManagedAccordionSection
            title={
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>{translate("Attributes")}</Typography>
                    <Link
                        className="top"
                        onClick={handleBackToTop}
                        {...styles.backToTop}
                        aria-label={translate("Back to Top")}
                    >
                        {translate("Back To Top")}
                    </Link>
                </StyledWrapper>
            }
            {...{ stopRowClickable: true }}
            initialExpanded={attributesFirst || (!renderingSpecifications && !renderingDocuments)}
            data-test-selector="productDetails_specifications_attributes"
            {...styles.attributeAccordionSection}
        >
            {/* WEST-361: Remove Brands */}
            {/* {brand && ( */}
            {/*    <StyledWrapper {...styles.attributeTypeWrapper}> */}
            {/*        <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography> */}
            {/*        <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography> */}
            {/*    </StyledWrapper> */}
            {/* )} */}
            {attributeTypes &&
                attributeTypes.map(attributeType => (
                    <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                        <StyledWrapper {...styles.attributeTextWrapper}>
                            <Typography {...styles.attributeTypeLabelText}>
                                {attributeType.label || attributeType.name}:&nbsp;
                            </Typography>
                            <Typography {...styles.attributeTypeValuesText}>
                                {attributeType
                                    .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                    .join(", ")}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
        </ManagedAccordionSection>
    );

    return (
        <>
            <Accordion
                headingLevel={2}
                data-test-selector="productDetails_specifications"
                {...styles.specificationsAccordion}
            >
                {productDetailsSpecification && <>{getSpecificationElement(productDetailsSpecification, true)}</>}
                {attributesFirst && attributesAccordion}
                {documentsFirst && documentsAccordion}
                {renderingSpecifications &&
                    specifications
                        ?.filter(specification => specification.sortOrder !== 1)
                        ?.map((specification, index) => {
                            return (
                                <React.Fragment key={specification.id.toString()}>
                                    {getSpecificationElement(
                                        specification,
                                        !productDetailsSpecification &&
                                            index === 0 &&
                                            (!documentsFirst || !renderingDocuments) &&
                                            (!attributesFirst || !renderingAttributes),
                                    )}
                                </React.Fragment>
                            );
                        })}
                {!attributesFirst && attributesAccordion}
                {!documentsFirst && documentsAccordion}
                {renderingVideos && (
                    <ManagedAccordionSection
                        initialExpanded={false}
                        key="videos"
                        title={
                            <StyledWrapper {...styles.titleWrapper}>
                                <Typography {...styles.title}>{translate("Videos")}</Typography>
                                <Link
                                    className="top"
                                    onClick={handleBackToTop}
                                    {...styles.backToTop}
                                    aria-label={translate("Back to Top")}
                                >
                                    {translate("Back To Top")}
                                </Link>
                            </StyledWrapper>
                        }
                        {...{ stopRowClickable: true }}
                        data-test-selector="productDetails_specifications_videos"
                        {...styles.videoAccordionSection}
                    >
                        <GridContainer gap={22}>
                            {images &&
                                images?.map((img, index) => {
                                    if (img.imageType.toLowerCase() !== "video") {
                                        return;
                                    }
                                    return (
                                        // XNG-Changes
                                        <GridItem key={img.id} width={[12, 6, 6, 4, 4]}>
                                            <ProductPrimaryVideo
                                                image={img}
                                                defaultImage={img.mediumImagePath}
                                                onClick={() => openModal(img)}
                                                extendedStyles={styles.primaryVideo}
                                            />
                                        </GridItem>
                                    );
                                })}
                        </GridContainer>
                    </ManagedAccordionSection>
                )}
            </Accordion>
            {/* XNG-Changes */}
            <Modal
                className="mobile-search-modal"
                isOpen={modalIsOpen}
                closeOnEsc={true}
                handleClose={() => {
                    setModalIsOpen(false);
                }}
                headline={translate("Watch")}
                {...styles.specificationsVideoModal}
            >
                <VideoPlayer imageUrl={image?.mediumImagePath} />
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
